// The room's tensor tab (design 01c 3.4): income and spending written by
// hand (the ledger), and the carts of 쇼핑. Helpers that do not touch the
// screen: months and days in Korea time, the entries kept by room, and the
// month's sums.
import type { LedgerEntry, LedgerKind } from '../../api/types'
import { kstParts } from '../cards/cards'

const WEEKDAYS = '일월화수목금토'
const two = (n: number) => String(n).padStart(2, '0')

// ---- months and days (Korea time) ----

/** "2026-10": the month ms falls in, in Korea. */
export function monthOf(ms: number): string {
  const p = kstParts(ms)
  return `${p.y}-${two(p.m)}`
}

/** "2026-10-05": the day ms falls on, in Korea. */
export function dayOf(ms: number): string {
  const p = kstParts(ms)
  return `${p.y}-${two(p.m)}-${two(p.d)}`
}

function parseMonth(month: string): { y: number; m: number } | null {
  const r = /^(\d{4})-(\d{2})$/.exec(month)
  if (!r) return null
  const y = Number(r[1])
  const m = Number(r[2])
  return m >= 1 && m <= 12 ? { y, m } : null
}

/** The month n months after (before, when negative). */
export function addMonths(month: string, n: number): string {
  const p = parseMonth(month)
  if (!p) return month
  const i = p.y * 12 + p.m - 1 + n
  return `${Math.floor(i / 12)}-${two((i % 12) + 1)}`
}

/** "2026년 10월" */
export function monthTitle(month: string): string {
  const p = parseMonth(month)
  return p ? `${p.y}년 ${p.m}월` : month
}

/** "10/5(월)" of "2026-10-05". */
export function entryDayLabel(day: string): string {
  const r = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (!r) return day
  const [y, m, d] = [Number(r[1]), Number(r[2]), Number(r[3])]
  return `${m}/${d}(${WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]})`
}

/** The day a new entry of the month starts from: today in its month,
    else the month's first day. */
export function defaultDay(month: string, now: number = Date.now()): string {
  return monthOf(now) === month ? dayOf(now) : `${month}-01`
}

/** A day input's value when it is a real day of 2000..2099. */
export function validDay(day: string): boolean {
  const r = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (!r) return false
  const [y, m, d] = [Number(r[1]), Number(r[2]), Number(r[3])]
  if (y < 2000 || y > 2099) return false
  const t = new Date(Date.UTC(y, m - 1, d))
  return t.getUTCMonth() === m - 1 && t.getUTCDate() === d
}

// ---- amounts ----

/** The largest amount an entry takes (the server's limit). */
export const AMOUNT_MAX = 10_000_000_000

/** Won typed as "3,000,000" or "3000000원"; null when empty, zero or too big. */
export function parseAmount(text: string): number | null {
  const v = text.replace(/[^0-9]/g, '')
  if (v === '' || v.length > 11) return null
  const n = Number(v)
  return n >= 1 && n <= AMOUNT_MAX ? n : null
}

/** "3,000,000" for an amount input (as typed: digits grouped). */
export function amountText(n: number | null): string {
  return n === null ? '' : n.toLocaleString('ko-KR')
}

// ---- entries in the store ----

/** The room's entries by id, kept by room id. */
export type Ledger = Record<number, Record<number, LedgerEntry>>

/** Keeps an entry unless an equal or newer version is there already
    (a deleted one stays, marked, so that an older copy does not return). */
export function putLedgerEntry(ledger: Ledger, entry: LedgerEntry): Ledger {
  const room = ledger[entry.room_id] ?? {}
  const old = room[entry.id]
  if (old && old.version >= entry.version) return ledger
  return { ...ledger, [entry.room_id]: { ...room, [entry.id]: entry } }
}

export function putLedgerEntries(ledger: Ledger, entries: LedgerEntry[]): Ledger {
  let out = ledger
  for (const e of entries) out = putLedgerEntry(out, e)
  return out
}

/** The month's entries of one kind, newest day first (on a day, the one
    written last first). */
export function entriesOf(
  room: Record<number, LedgerEntry> | undefined,
  month: string,
  kind: LedgerKind,
): LedgerEntry[] {
  if (!room) return []
  return Object.values(room)
    .filter((e) => !e.deleted && e.kind === kind && e.day.startsWith(`${month}-`))
    .sort((a, b) => (a.day === b.day ? b.created_at - a.created_at || b.id - a.id : a.day < b.day ? 1 : -1))
}

export interface MonthTotals {
  income: number
  expense: number
  /** What the carts really cost that month (from the server). */
  shopping: number
  /** income - expense - shopping */
  balance: number
}

/** The month's sums from the entries kept here (they change live) and the
    carts' amount the server counted. */
export function monthTotals(room: Record<number, LedgerEntry> | undefined, month: string, shopping: number): MonthTotals {
  let income = 0
  let expense = 0
  for (const e of Object.values(room ?? {})) {
    if (e.deleted || !e.day.startsWith(`${month}-`)) continue
    if (e.kind === 'income') income += e.amount
    else expense += e.amount
  }
  return { income, expense, shopping, balance: income - expense - shopping }
}

/** "-250,000원" as well as "4,500원". */
export function signedWon(n: number): string {
  return `${n < 0 ? '-' : ''}${Math.abs(n).toLocaleString('ko-KR')}원`
}
